/**
 * /demos — galería de DEMOS de funciones futuras de OPUS ci (solo ci, solo staff Ecosoft).
 * Sirve para que dirección vea cómo funcionarían antes de decidir si se esperan para el lanzamiento.
 * Todo es con un modelo público de ejemplo y datos de ejemplo, y así se dice en cada pantalla.
 */
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Button, Tooltip } from '@fluentui/react-components';
import { AppTopBar } from '../../components/AppTopBar.js';
import { CartelDemo } from '../../components/demos/DemoMarco.js';
import { productoActivo } from '../../lib/productoActivo.js';
import catalogo from '../../components/demos/datos/catalogo.json';
import { DemoAvance4D } from './DemoAvance4D.js';
import { DemoCambioDePlano } from './DemoCambioDePlano.js';
import { DemoChequeoModelo } from './DemoChequeoModelo.js';
import { DemoQueMeFalta } from './DemoQueMeFalta.js';
import { DemoPresupuestoUnClic } from './DemoPresupuestoUnClic.js';
import { DemoEnlaceCliente } from './DemoEnlaceCliente.js';
import { DemoDosOpciones } from './DemoDosOpciones.js';
import { DemoNumerosGeneradores } from './DemoNumerosGeneradores.js';

type EstadoDemo = 'solo-demo' | 'en-construccion' | 'parcial' | 'ya-en-ci';

// Etiqueta de cada tarjeta: dice si la función ya existe en OPUS ci con los planos del usuario.
const ESTADOS: Record<EstadoDemo, { texto: string; color: string; fondo: string }> = {
  'solo-demo': { texto: 'Solo demo', color: 'var(--text-secondary)', fondo: 'var(--bg-raised)' },
  'en-construccion': { texto: 'En construcción', color: 'var(--warn)', fondo: 'var(--warn-weak)' },
  parcial: { texto: 'Parcialmente en OPUS ci', color: 'var(--warn)', fondo: 'var(--warn-weak)' },
  'ya-en-ci': { texto: 'Ya en OPUS ci', color: 'var(--ok)', fondo: 'var(--ok-weak)' },
};

interface DemoDelCatalogo {
  id: string;
  idea: number;
  nombre: string;
  dolor: string;
  estado: EstadoDemo;
  /** Estado «parcial»: lo que todavía no está hecho (se muestra en el tooltip del chip). */
  faltante?: string[];
  /** Cuando ya está (o está parcialmente) en ci: a dónde lleva el botón de la función real. */
  funcionReal?: { ruta: string; texto: string };
}

const DEMOS_DEL_CATALOGO = catalogo.demos as DemoDelCatalogo[];

const DEMOS: Record<string, () => JSX.Element> = {
  'avance-4d': DemoAvance4D,
  'cambio-de-plano': DemoCambioDePlano,
  'chequeo-del-modelo': DemoChequeoModelo,
  'que-me-falta': DemoQueMeFalta,
  'presupuesto-en-un-clic': DemoPresupuestoUnClic,
  'enlace-para-tu-cliente': DemoEnlaceCliente,
  'dos-opciones': DemoDosOpciones,
  'numeros-generadores': DemoNumerosGeneradores,
};

export function DemosPage() {
  const navigate = useNavigate();
  if (productoActivo() !== 'ci') return <Navigate to="/projects" replace />;
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)', color: 'var(--text-primary)' }}>
      <AppTopBar />
      <CartelDemo />
      <div style={{ maxWidth: 1180, margin: '0 auto', padding: '24px 24px 48px' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-600)', margin: 0 }}>{catalogo.titulo}</h1>
        <p style={{ color: 'var(--text-secondary)', margin: '6px 0 20px', maxWidth: 760 }}>{catalogo.subtitulo}</p>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: 14,
          }}
        >
          {DEMOS_DEL_CATALOGO
            .filter((d) => DEMOS[d.id])
            .map((d) => {
              const estado = ESTADOS[d.estado] ?? ESTADOS['solo-demo'];
              return (
              <article
                key={d.id}
                style={{
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-md)',
                  padding: 16,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                <div style={{ fontSize: 'var(--fs-200)', color: 'var(--text-tertiary)' }}>Idea {d.idea}</div>
                <h2 style={{ margin: 0, fontSize: 'var(--fs-400)', fontWeight: 600 }}>{d.nombre}</h2>
                <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 'var(--fs-300)', flex: 1 }}>{d.dolor}</p>
                {(() => {
                  const chip = (
                    <div
                      tabIndex={d.faltante?.length ? 0 : undefined}
                      style={{
                        fontSize: 'var(--fs-200)',
                        color: estado.color,
                        background: estado.fondo,
                        borderRadius: 'var(--radius-sm)',
                        padding: '2px 8px',
                        alignSelf: 'flex-start',
                        cursor: d.faltante?.length ? 'help' : undefined,
                      }}
                    >
                      {estado.texto}
                      {d.faltante?.length ? ' ⓘ' : ''}
                    </div>
                  );
                  if (!d.faltante?.length) return chip;
                  return (
                    <Tooltip
                      relationship="description"
                      positioning="below-start"
                      content={{
                        children: (
                          <div style={{ maxWidth: 340 }}>
                            <b>Ya funciona una parte. Falta por hacer:</b>
                            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                              {d.faltante.map((f) => (
                                <li key={f}>{f}</li>
                              ))}
                            </ul>
                          </div>
                        ),
                      }}
                    >
                      {chip}
                    </Tooltip>
                  );
                })()}
                {(d.estado === 'ya-en-ci' || d.estado === 'parcial') && d.funcionReal ? (
                  <Button appearance="primary" onClick={() => navigate(d.funcionReal!.ruta)}>
                    {d.funcionReal.texto}
                  </Button>
                ) : null}
                <Button
                  appearance={(d.estado === 'ya-en-ci' || d.estado === 'parcial') && d.funcionReal ? 'secondary' : 'primary'}
                  onClick={() => navigate(`/demos/${d.id}`)}
                >
                  Ver demo
                </Button>
              </article>
              );
            })}
        </div>
      </div>
    </div>
  );
}

export function DemoPage() {
  const { demoId } = useParams();
  if (productoActivo() !== 'ci') return <Navigate to="/projects" replace />;
  const Demo = demoId ? DEMOS[demoId] : undefined;
  if (!Demo) return <Navigate to="/demos" replace />;
  return <Demo />;
}
